import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@daater/ui';
import { useRegisterUserMutation } from '../api/queries';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';

export default function RegisterScreen() {
  const navigate = useNavigate();
  const registerMutation = useRegisterUserMutation();
  const [submitted, setSubmitted] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const profileData = React.useMemo<Record<string, unknown>>(() => {
    try {
      const stored = sessionStorage.getItem('daater_signup_profile');
      return stored ? (JSON.parse(stored) as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }, []);

  const fullName = (profileData.fullName as string | undefined) ?? '';
  const email = (profileData.email as string | undefined) ?? '';
  const password = (profileData.password as string | undefined) ?? '';

  React.useEffect(() => {
    if (!fullName || !email || !password) {
      void navigate({ to: '/signup/demo', replace: true });
    }
  }, [fullName, email, password, navigate]);

  const handleSubmit = async () => {
    setErrorMessage(null);
    try {
      await registerMutation.mutateAsync({ fullName, email, password });
      sessionStorage.removeItem('daater_signup_profile');
      setSubmitted(true);
    } catch (error) {
      setErrorMessage((error as Error).message || 'Error al registrar');
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pale-grey px-4">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          <div className="rounded-md border border-greenish-teal/30 bg-greenish-teal/10 px-4 py-4 text-sm">
            <p className="font-semibold text-charcoal-grey">¡Registro completado!</p>
            <p className="mt-2 text-slate">
              Tu cuenta está siendo revisada. Un asesor la activará pronto.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void navigate({ to: '/login' })}
            className="mt-4 text-xs text-slate underline"
          >
            Ir al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="hidden bg-[#1a2e58] px-12 text-white lg:flex lg:w-1/2 lg:flex-col lg:items-center lg:justify-center">
        <div className="mx-auto max-w-md space-y-8">
          <DaaterLogoHomeLink
            alt="Daater"
            className="inline-flex w-fit"
            imgClassName="h-12 brightness-0 invert"
          />
          <blockquote className="border-l-2 border-white/30 pl-5 text-lg leading-relaxed text-white/80">
            Completa el registro para comenzar a usar Daater.
          </blockquote>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-pale-grey px-4 py-8 lg:w-1/2">
        <div className="w-full max-w-md rounded-xl border border-pale-grey-three bg-white p-6 shadow-sm">
          <div className="mb-6 flex justify-center lg:hidden">
            <DaaterLogoHomeLink alt="Daater" imgClassName="h-10" />
          </div>

          <h1 className="text-2xl font-semibold text-charcoal-grey">Completar registro</h1>
          <p className="mt-1 text-sm text-slate">Confirma tus datos para crear tu cuenta.</p>

          <div className="mt-6 space-y-4">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <p>
                <span className="font-medium">Nombre:</span> {fullName}
              </p>
              <p className="mt-1">
                <span className="font-medium">Correo:</span> {email}
              </p>
            </div>

            {errorMessage ? (
              <p className="rounded-md border border-darkish-pink/30 bg-darkish-pink/10 px-3 py-2 text-xs text-darkish-pink">
                {errorMessage}
              </p>
            ) : null}

            <Button
              type="button"
              className="w-full"
              disabled={registerMutation.isPending}
              onClick={() => void handleSubmit()}
            >
              {registerMutation.isPending ? 'Registrando...' : 'Crear cuenta'}
            </Button>
          </div>

          <button
            type="button"
            onClick={() => void navigate({ to: '/login' })}
            className="mt-4 text-xs text-slate underline"
          >
            Ya tengo cuenta
          </button>
        </div>
      </div>
    </div>
  );
}
